import { useState } from "react";
import { Link } from "react-router-dom";
import BlogCard from "../components/BlogCard";
import { useGetBlogsQuery } from "../store/api/blogsApi";

export default function BlogsList() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useGetBlogsQuery();
  const allBlogs = data?.data || [];
  const categories = ["All", ...(data?.meta?.categories || [])];

  const filtered = allBlogs.filter((b) => {
    const matchCat = activeCategory === "All" || b.category === activeCategory;
    const matchSearch =
      !search.trim() ||
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      (b.excerpt || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div>
      {/* Hero */}
      <div className="relative bg-[#1a7b7b] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-10 -right-10 w-52 md:w-72 opacity-80"
            style={{
              background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 40%, #c3f0e8 70%, #b8d4f8 100%)",
              borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
              height: "220px",
              filter: "blur(2px)",
            }}
          />
          <div
            className="absolute -bottom-10 -left-10 w-40 md:w-56 opacity-60"
            style={{
              background: "linear-gradient(135deg, #c3f0e8 0%, #a8edea 50%, #fed6e3 100%)",
              borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%",
              height: "180px",
              filter: "blur(2px)",
            }}
          />
        </div>
        <div className="container mx-auto px-4 py-14 md:py-20 flex flex-col items-center gap-4 relative z-10">
          <div className="flex flex-wrap items-center justify-center gap-1 text-white/70 font-manrope-regular text-sm">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Blog</span>
          </div>
          <h1 className="font-manrope-bold text-[28px] md:text-[42px] text-white text-center">
            Stories & Insights
          </h1>
          <p className="font-manrope-regular text-sm text-white/70 text-center max-w-md">
            Tips, travel guides, and updates from Contentour Bonaire.
          </p>
          {/* Search */}
          <div className="w-full max-w-lg flex items-center bg-white rounded-2xl px-3 py-2 gap-3 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400 flex-shrink-0">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search articles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none font-manrope-regular text-base text-gray-700 placeholder:text-gray-400 bg-transparent"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-bg py-12 md:py-16">
        <div className="container mx-auto px-4 flex flex-col gap-8">

          {/* Category pills */}
          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`font-manrope-medium text-sm px-4 py-1.5 rounded-full border transition-colors ${
                    activeCategory === cat
                      ? "bg-primary text-white border-primary"
                      : "bg-white text-secondary border-border hover:border-primary hover:text-primary"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Result count */}
          {!isLoading && (
            <p className="font-manrope-regular text-sm text-secondary">
              Showing{" "}
              <span className="font-manrope-semibold text-black">{filtered.length}</span>{" "}
              article{filtered.length !== 1 ? "s" : ""}
            </p>
          )}

          {/* Loading skeletons */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-[16/9] bg-gray-200" />
                  <div className="p-5 flex flex-col gap-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-full" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="flex flex-col items-center gap-3 py-24">
              <p className="font-manrope-bold text-lg text-secondary">Failed to load blog posts.</p>
              <p className="font-manrope-regular text-sm text-gray-400">Please try again later.</p>
            </div>
          )}

          {/* Grid */}
          {!isLoading && !isError && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((blog) => (
                <BlogCard key={blog.id || blog._id} blog={blog} />
              ))}
            </div>
          )}

          {/* Empty */}
          {!isLoading && !isError && filtered.length === 0 && (
            <div className="flex flex-col items-center gap-4 py-24">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-14 h-14 text-gray-300">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
              </svg>
              <p className="font-manrope-bold text-lg text-secondary">No articles found</p>
              <p className="font-manrope-regular text-sm text-gray-400">Try a different keyword or category</p>
              {activeCategory !== "All" && (
                <button
                  onClick={() => setActiveCategory("All")}
                  className="mt-2 bg-primary text-white font-manrope-semibold text-sm px-6 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                >
                  View all articles
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
